import { Delete, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAccountUsers } from "../../../hooks/Users";
import { useEquipmentImages } from "../../../hooks/Warehouse";
import { EquipmentFormType } from "../../../types/forms/warehouse/equipment";
import { EquipmentResponse } from "../../../types/model/warehouse/equipment";
import EquipmentForm from "./EquipmentForm";
import MaintenanceChip from "./MaintenanceChip";
import StatusChip from "./StatusChip";

interface RowActionsProps {
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onDelete: () => void;
}

const RowActions = ({ formId, isEditing, isSaving, isDeleting, onEdit, onCancel, onDelete }: RowActionsProps) => {
  const { t } = useTranslation();

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}
    >
      {isEditing ? (
        <>
          <Tooltip title={t("generic.cancel")}>
            <IconButton size="small" color="error" onClick={onCancel} disabled={isSaving}>
              <EditOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("generic.save")}>
            <IconButton size="small" color="success" type="submit" form={formId} disabled={isSaving}>
              {isSaving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <>
          <Tooltip title={t("generic.edit")}>
            <IconButton size="small" color="primary" onClick={onEdit} disabled={isDeleting}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("generic.delete")}>
            <IconButton size="small" color="error" onClick={onDelete} disabled={isDeleting}>
              {isDeleting ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

interface EquipmentRowProps {
  row: EquipmentResponse;
  open: boolean;
  canEdit: boolean;
  columnCount: number;
  onToggle: () => void;
  onUpdate: (equipmentId: number, values: EquipmentFormType) => Promise<void>;
  onDelete: (equipmentId: number) => Promise<void>;
}

export default function EquipmentRow({
  row,
  open,
  canEdit,
  columnCount,
  onToggle,
  onUpdate,
  onDelete,
}: Readonly<EquipmentRowProps>) {
  const theme = useTheme();
  const formId = `form-${row.id}`;

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const images = useEquipmentImages(row.id, open);
  const users = useAccountUsers([row.accountId, row.sharedAccountId], open);

  const handleSave = async (values: EquipmentFormType) => {
    try {
      setIsSaving(true);
      await onUpdate(row.id, values);
      setIsEditing(false);
    } catch {
      // Error already handled by parent, stay in edit mode
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete(row.id);
    } catch {
      // Error already handled by parent
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = () => {
    if (!open) onToggle();
    setIsEditing(true);
  };

  return (
    <React.Fragment>
      <TableRow onClick={onToggle} sx={{ cursor: "pointer", "& > *": { borderBottom: "unset" } }}>
        <TableCell>{open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="medium">
            {row.equipmentType}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[row.equipmentBrandName, row.equipmentModel].filter(Boolean).join(" · ")}
          </Typography>
        </TableCell>
        <TableCell>{row.serialNumber}</TableCell>
        <TableCell>{row.location}</TableCell>
        <TableCell>
          <StatusChip status={row.status} />
        </TableCell>
        <TableCell>
          <MaintenanceChip date={row.maintenanceDate} daysBefore={row.maintenanceAlertDaysBefore} />
        </TableCell>
        {canEdit && (
          <TableCell align="right">
            <RowActions
              formId={formId}
              isEditing={isEditing}
              isSaving={isSaving}
              isDeleting={isDeleting}
              onEdit={handleEdit}
              onCancel={() => setIsEditing(false)}
              onDelete={handleDelete}
            />
          </TableCell>
        )}
      </TableRow>
      <TableRow sx={open ? { backgroundColor: theme.palette.action.hover } : {}}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columnCount}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <EquipmentForm
                equipmentData={row}
                formId={formId}
                isEditing={isEditing}
                isSaving={isSaving}
                isDeleting={isDeleting}
                images={images.images}
                isLoadingImages={images.isLoading}
                isUploadingImage={images.isUploading}
                deletingImageId={images.deletingId}
                onUploadImage={images.upload}
                onDeleteImage={images.remove}
                users={users.users}
                isLoadingUsers={users.isLoading}
                onSubmit={handleSave}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}